import {
  HtmlContent,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
  useTranslation,
  WidthToggleIcon
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import type { RenderableItem } from '@paintscout/util/builder'
import { getObjectLabels, getQuoteOptions } from '@paintscout/util/builder'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined'
import React from 'react'
import striptags from 'striptags'
import { isHtmlEmpty } from '../presentation/util/isHtmlEmpty'
import { useQuote } from '../context'
import { useTranslatedNoteContent } from '../Quote/views/WorkOrderView/useTranslatedNoteContent'
import { splitTrailingMeasurement } from '../Quote/views/WorkOrderView/WorkOrderCard/splitTrailingMeasurement'
import { translateStructuredWorkOrderContent } from '../Quote/views/WorkOrderView/WorkOrderCard/translateStructuredWorkOrderContent'

export interface WorkOrderCardNextProps {
  item: RenderableItem
  /** Show the per-row hour values (respects the section's "Show all hours" setting). */
  showHours?: boolean
  /** Show the "Total → {labour}: X" footer. */
  showTotalHours?: boolean
  /** Hover-revealed drag handle (a react-sortable-hoc SortableHandle supplied by the section). */
  dragHandle?: React.ReactNode
  /** Whether this card currently spans both grid columns (drives the width-toggle icon state). */
  isFullWidth?: boolean
  /** Hide this card into the section's "Hidden" panel. */
  onToggleHide?: () => void
  /** Toggle this card between half- and full-width. */
  onToggleWidth?: () => void
  onClick?: (ev: React.MouseEvent<HTMLElement>) => void
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    // Outline vs inner rule: the card's edge is the heavier P85, the rules between rows the lighter P90.
    const outline = `1px solid ${neutral.P85}`
    const rule = `1px solid ${neutral.P90}`
    return {
      // One bordered stack — header, rows, then the Total bar in its own tray. `xl` (12px) is the
      // closest token to the mock's 10px corners; `overflow: hidden` clips the header and tray to it.
      root: {
        border: outline,
        borderRadius: theme.borderRadius.xl,
        backgroundColor: neutral.White,
        overflow: 'hidden',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        '&:hover $dragHandleWrap, &:hover $controls, &:hover $controlsDivider': {
          opacity: 1
        },
        // Hovering anywhere on the card tints its rows, so it reads as one target.
        '&:hover $row, &:hover $rowDivider, &:hover $footerTray': {
          backgroundColor: neutral.P98
        }
      },
      // Light-gray card header: name on the left, the "hr" unit on the right (matches the mock).
      // Relative so the drag handle can be absolutely centered without shifting the name. No bottom
      // border — the first row carries the outline-weight rule instead.
      header: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        minHeight: 44,
        padding: theme.spacing(1.25, 2),
        backgroundColor: neutral.P96
      },
      // Drag handle, centered and revealed on card hover.
      dragHandleWrap: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        color: neutral.P60,
        cursor: 'grab',
        opacity: 0,
        transition: 'opacity 120ms ease',
        '& svg': { fontSize: 18 }
      },
      headerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        flexShrink: 0,
        zIndex: 1
      },
      // Hide + width controls, revealed on card hover (the hr label stays visible).
      controls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        opacity: 0,
        transition: 'opacity 120ms ease'
      },
      controlButton: {
        padding: 1,
        color: neutral.P60,
        '& svg': { fontSize: 18 }
      },
      // Separates the controls from the "hr" label, and fades in with them.
      controlsDivider: {
        width: 1,
        height: 22,
        backgroundColor: neutral.P85,
        flexShrink: 0,
        opacity: 0,
        transition: 'opacity 120ms ease'
      },
      name: {
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(15),
        color: neutral.P35,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      subtitle: {
        color: neutral.P60,
        fontWeight: 400,
        marginLeft: theme.spacing(0.5)
      },
      hr: {
        color: neutral.P60,
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(16),
        flexShrink: 0
      },
      body: {},
      row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.25),
        padding: theme.spacing(2),
        transition: 'background-color 150ms ease',
        // Outline-weight rule under the header; the rules between rows are inset (see rowDivider).
        '&:first-child': {
          borderTop: outline
        }
      },
      // Between-row rule, inset from the card edges with a little air above and below.
      rowDivider: {
        padding: theme.spacing(0.5, 2),
        transition: 'background-color 150ms ease'
      },
      rowDividerLine: {
        borderTop: rule
      },
      rowMain: {
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5)
      },
      subName: {
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P40,
        lineHeight: 1.4
      },
      subLine: {
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P40,
        lineHeight: 1.4
      },
      rowValue: {
        flexShrink: 0,
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P40
      },
      // The Total bar sits in a thin tray at the bottom of the card, so the bar's own outline reads
      // inside the card's (matches the mock).
      footerTray: {
        padding: theme.spacing(0.5),
        transition: 'background-color 150ms ease'
      },
      footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        minHeight: 44,
        padding: theme.spacing(0, 1.5, 0, 2),
        border: outline,
        // `lg` (8px) is the closest token to the mock's 6px footer bar.
        borderRadius: theme.borderRadius.lg,
        backgroundColor: neutral.White,
        // With the label gone on phones (see footerLabel), the breakdown centers in the bar.
        [theme.breakpoints.down('xs')]: {
          justifyContent: 'center',
          padding: theme.spacing(0, 1.5)
        }
      },
      footerLabel: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 500,
        color: neutral.P40,
        // Phones drop it: the breakdown already ends in "Total: N", and on a narrow card the leading
        // label is what pushed that number off the end of the bar.
        [theme.breakpoints.down('xs')]: {
          display: 'none'
        }
      },
      footerTotal: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 500,
        color: neutral.P40,
        whiteSpace: 'nowrap'
      },
      // "Prep: 4 | Painting: 29 | Total: 33" — the parts joined by thin vertical rules (matches the mock).
      footerBreakdown: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexShrink: 0,
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        [theme.breakpoints.down('xs')]: {
          justifyContent: 'center'
        }
      },
      footerDivider: {
        width: 1,
        height: 11,
        borderRadius: theme.borderRadius.full,
        backgroundColor: neutral.P80,
        flexShrink: 0
      }
    }
  },
  { name: 'WorkOrderCardNext' }
)

/** Resolve an item/sub-item name that may be a plain string or an overridable {useCustom,custom,default}. */
function resolveName(name: RenderableItem['name']): string {
  if (!name) {
    return ''
  }
  if (typeof name === 'string') {
    return name
  }
  return (name.useCustom ? name.custom : name.default) ?? ''
}

function formatHours(value: string | number | undefined): string {
  const n = typeof value === 'string' ? parseFloat(value) : value
  return Number.isFinite(n as number) ? String(n) : ''
}

/** Coerce an hour field (string | number | undefined) to a number; anything unusable becomes 0. */
function toHours(value: unknown): number {
  const n = typeof value === 'string' ? parseFloat(value) : (value as number)
  return Number.isFinite(n) ? n : 0
}

/** Trim a trailing .0 so whole hours read "4", not "4.0" (matches the mock). */
function formatHourTotal(value: number): string {
  return value % 1 === 0 ? String(value) : value.toFixed(1)
}

/**
 * The crew description is "{details}\n{product}\n{product}…". The crew item builders hand us those
 * pieces on `additionalFields.crewDescriptionParts`; splitting the joined string is the fallback for
 * anything that predates them (or a non-crew consumer).
 */
function splitCrewDescription(
  description: string,
  parts?: { details?: string; products?: string[] }
): { details: string; products: string[] } {
  if (parts) {
    return { details: parts.details ?? '', products: parts.products ?? [] }
  }
  const [details = '', ...products] = description
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
  return { details, products }
}

/**
 * The redesigned Work Order hour card (estimateNext) — a light-header card matching the mock: the
 * area/substrate name + "hr" unit in the header, then one row per sub-item (name, location, Coats)
 * with its hours on the right, and a "Total → {labour}: X" footer. Hovering the header reveals a
 * center drag handle plus hide / half-full-width controls (supplied by the parent section).
 */
export default function WorkOrderCardNext({
  item,
  showHours,
  showTotalHours,
  dragHandle,
  isFullWidth,
  onToggleHide,
  onToggleWidth,
  onClick
}: WorkOrderCardNextProps) {
  const classes = useStyles()
  const { __ } = useTranslation()
  const { quote } = useQuote()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const { showWorkOrderViewDescriptions, hideWorkOrderDimensions, showWorkOrderProducts } = getQuoteOptions({
    quote,
    options
  })
  const showCoats = options.options.quotes?.showWorkOrderCoats

  const name = resolveName(item.name)
  const subItems = item.subItems ?? []
  const hasSubItems = subItems.length > 0

  const rows = hasSubItems
    ? subItems.map((subItem) => ({
        key: subItem.key ?? subItem.name,
        name: subItem.name,
        ...splitCrewDescription(subItem.description ?? '', subItem.additionalFields?.crewDescriptionParts),
        // Crew notes always show; the area/substrate report text is gated by the account's
        // "work order descriptions" setting (same split the legacy WorkOrderCard used).
        crewNote: subItem.crewNote ?? '',
        reportText: subItem.reportText ?? '',
        coats: subItem.additionalFields?.coats ? String(subItem.additionalFields.coats) : '',
        prep: toHours(subItem.additionalFields?.prep),
        painting: toHours(subItem.additionalFields?.painting),
        value: subItem.value
      }))
    : [
        {
          key: item.key,
          name: '',
          details: resolveName(item.name)
            ? (item.description?.useCustom ? item.description?.custom : item.description?.default) ?? ''
            : '',
          products: [] as string[],
          crewNote: '',
          reportText: '',
          coats: '',
          prep: toHours(item.additionalFields?.prep),
          painting: toHours(item.additionalFields?.painting),
          value: item.value
        }
      ]

  // Translate the item card content the same way the legacy WorkOrderCard did: item/sub-item names
  // and descriptions go through the live note-translation batch (customer view) / pre-baked map, and
  // then through translateStructuredWorkOrderContent for the structured "coats"/measurement labels.
  // The trailing measurement on a description is split off so only the prose is sent for translation.
  // All inputs are gathered into one stable array (hooks can't run per-row) and mapped back in order.
  // Products go through as one newline-joined block (split back apart after translating) so each row
  // contributes a fixed number of inputs regardless of how many products it has.
  const rowProductParts = rows.map((row) => splitTrailingMeasurement(row.products.join('\n')))
  const translationInputs = [
    name,
    objectLabels.labour.value,
    objectLabels.prep.value,
    ...rows.flatMap((row, i) => [row.name || '', row.details, rowProductParts[i].base, row.crewNote, row.reportText])
  ]
  const [translatedInputs] = useTranslatedNoteContent(translationInputs)
  let ti = 0
  const translatedName = translateStructuredWorkOrderContent(translatedInputs[ti++] || name, __)
  const translatedLabour = translateStructuredWorkOrderContent(translatedInputs[ti++] || objectLabels.labour.value, __)
  const translatedPrep = translateStructuredWorkOrderContent(translatedInputs[ti++] || objectLabels.prep.value, __)
  const translatedRows = rows.map((row, i) => {
    const translatedRowName = translateStructuredWorkOrderContent(translatedInputs[ti++] || row.name || '', __)
    const translatedDetails = translateStructuredWorkOrderContent(translatedInputs[ti++] || row.details, __)
    const translatedProductBase = translatedInputs[ti++] || rowProductParts[i].base
    const translatedProducts = translateStructuredWorkOrderContent(
      translatedProductBase + rowProductParts[i].measurement,
      __
    )
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
    const translatedCrewNote = translateStructuredWorkOrderContent(translatedInputs[ti++] || row.crewNote, __)
    const translatedReportText = translateStructuredWorkOrderContent(translatedInputs[ti++] || row.reportText, __)
    return {
      ...row,
      name: translatedRowName,
      details: translatedDetails,
      products: translatedProducts,
      crewNote: translatedCrewNote,
      reportText: translatedReportText
    }
  })

  // Show the trailing "Total" footer when there's more than one row or the area total differs from
  // the summed sub-items (mirrors the legacy WorkOrderCard's showTotal heuristic).
  const sumRows = (field: 'prep' | 'painting') => rows.reduce((total, row) => total + row[field], 0)

  // Footer hour split: prefer the item's own prep/painting fields (they survive overrides), falling
  // back to summing the rows. "Total" stays the item value so the footer always agrees with the
  // header/quote totals even when a prep+painting sum wouldn't.
  const prepTotal = item.additionalFields?.prep != null ? toHours(item.additionalFields.prep) : sumRows('prep')
  const paintingTotal =
    item.additionalFields?.painting != null ? toHours(item.additionalFields.painting) : sumRows('painting')
  const hoursTotal = toHours(item.value)
  // The split only earns its place when it actually reconciles with the total. It won't when the
  // item's hours are overridden (`item.value` is authoritative and wins), and three numbers that
  // don't add up read as a bug to a crew — so in that case the footer shows the total alone.
  const splitAddsUp = Math.abs(prepTotal + paintingTotal - hoursTotal) < 0.005
  const footerParts = [
    ...(splitAddsUp
      ? [`${translatedPrep}: ${formatHourTotal(prepTotal)}`, `${translatedLabour}: ${formatHourTotal(paintingTotal)}`]
      : []),
    `${__('Total')}: ${formatHourTotal(hoursTotal)}`
  ]

  const showTotal =
    showTotalHours &&
    (subItems.length > 1 ||
      (item?.value !== 0 &&
        item?.subItemTotal != null &&
        Number(item?.value).toFixed(2) !== Number(item?.subItemTotal).toFixed(2)))

  return (
    <div className={classes.root} onClick={onClick} role={onClick ? 'button' : undefined}>
      <div className={classes.header}>
        <Typography className={classes.name}>
          {translatedName}
          {item.subtitle && !hideWorkOrderDimensions && <span className={classes.subtitle}>({item.subtitle})</span>}
        </Typography>
        {dragHandle && (
          // `disableWrapper` matters here: Tooltip otherwise wraps children in a MUI Badge, whose
          // root is `position: relative`, so the handle's absolute centering would resolve against
          // that zero-width Badge (mid-flex-row) instead of the header — drifting with title length.
          <Tooltip content="Drag to reorder" placement="top" hideIcon disableWrapper>
            <span className={classes.dragHandleWrap}>{dragHandle}</span>
          </Tooltip>
        )}
        <div className={classes.headerRight}>
          <span className={classes.controls}>
            {onToggleHide && (
              <IconButton
                size="small"
                className={classes.controlButton}
                aria-label="Hide card"
                onClick={(ev) => {
                  ev.stopPropagation()
                  onToggleHide()
                }}
              >
                <VisibilityOffIcon />
              </IconButton>
            )}
            {onToggleWidth && (
              <IconButton
                size="small"
                className={classes.controlButton}
                aria-label={isFullWidth ? 'Make card half width' : 'Make card full width'}
                onClick={(ev) => {
                  ev.stopPropagation()
                  onToggleWidth()
                }}
              >
                <WidthToggleIcon />
              </IconButton>
            )}
          </span>
          {(onToggleHide || onToggleWidth) && <span className={classes.controlsDivider} />}
          <span className={classes.hr}>hr</span>
        </div>
      </div>

      <div className={classes.body}>
        {translatedRows.map((row, rowIndex) => (
          <React.Fragment key={row.key}>
            {/* Inset rule between rows (the first row's top border comes from the header instead). */}
            {rowIndex > 0 && (
              <div className={classes.rowDivider}>
                <div className={classes.rowDividerLine} />
              </div>
            )}
            <div className={classes.row}>
              <div className={classes.rowMain}>
                {row.name && <Typography className={classes.subName}>{row.name}</Typography>}
                {/* The crew description (prep/labour, then one line per product) always shows —
                    matching the legacy card. Only the area/substrate report text sits behind the
                    account setting; the product lines follow the nav bar's "Hide all products".
                    Rendered as HTML because a line item's description is rich text (the crew-built
                    strings are plain, which HtmlContent passes through and pre-wraps itself). */}
                {!isHtmlEmpty(row.details) && (
                  <Typography component="div" className={classes.subLine}>
                    <HtmlContent content={row.details} />
                  </Typography>
                )}
                {/* Index keys: two different products can format to the same string (same label,
                    colour and volume), which would collide on a string key. */}
                {showWorkOrderProducts !== false &&
                  row.products.map((product, productIndex) => (
                    <Typography className={classes.subLine} key={productIndex}>
                      {product}
                    </Typography>
                  ))}
                {showWorkOrderViewDescriptions && !isHtmlEmpty(row.reportText) && (
                  <Typography component="div" className={classes.subLine}>
                    <HtmlContent content={row.reportText} />
                  </Typography>
                )}
                {!!striptags(row.crewNote).trim() && (
                  <Typography component="div" className={classes.subLine}>
                    <HtmlContent classes={{ root: 'crew-note-highlight' }} content={row.crewNote} />
                  </Typography>
                )}
                {showCoats && row.coats && (
                  <Typography className={classes.subLine}>{`${__('Coats')}: ${row.coats}`}</Typography>
                )}
              </div>
              {showHours && <span className={classes.rowValue}>{formatHours(row.value)}</span>}
            </div>
          </React.Fragment>
        ))}
      </div>

      {showTotal && (
        <div className={classes.footerTray}>
          <div className={classes.footer}>
            <span className={classes.footerLabel}>{__('Total')}</span>
            <span className={classes.footerBreakdown}>
              {/* Index keys — `objectLabels.labour` is account-configurable, so two parts can render
                  the same string (rename labour to "Prep" and the values match). */}
              {footerParts.map((part, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <span className={classes.footerDivider} />}
                  <span className={classes.footerTotal}>{part}</span>
                </React.Fragment>
              ))}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
